// Paddle.js v2 (#1680, R28): carga única, inicialización única y checkout overlay.
//
// Paddle es el Merchant of Record fuera de Chile: la tarjeta se escribe DENTRO
// de su iframe, nunca en un campo nuestro. Acá solo se abre con el `price_id`,
// el correo y el `custom_data` ({user_id}) que devuelve el POST del backend.
//
// La CSP de `public/_headers` ya permite `cdn.paddle.com` (script-src, style-src)
// y `buy.paddle.com` / `sandbox-buy.paddle.com` (frame-src), con el
// Permissions-Policy `payment` de R34. Esos orígenes salen de leer el Paddle.js
// real, no de la documentación: los de frame y connect (y si el overlay pide algo
// más) se validan en SANDBOX con la consola abierta antes de cobrar.
export const PADDLE_JS_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";

let promesaCarga = null;
let inicializadoCon = null;
let manejadorActual = null;

// Un solo <script>, promesa memoizada. Si la carga falla (red del hospital, CSP)
// se olvida la promesa para poder reintentar desde el botón.
export const cargarPaddle = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("sin_window"));
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (promesaCarga) return promesaCarga;
  promesaCarga = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PADDLE_JS_URL;
    script.async = true;
    script.setAttribute("data-paddle-js", "1");
    script.onload = () => {
      if (window.Paddle) resolve(window.Paddle);
      else {
        promesaCarga = null;
        reject(new Error("paddle_no_definido"));
      }
    };
    script.onerror = () => {
      promesaCarga = null;
      script.remove();
      reject(new Error("paddle_no_cargo"));
    };
    document.head.appendChild(script);
  });
  return promesaCarga;
};

// `Paddle.Initialize` se llama una vez por token; los eventos llegan siempre al
// manejador del checkout en curso (el callback inicial no se puede reemplazar).
const inicializar = (Paddle, { client_token: token, environment }) => {
  if (inicializadoCon === token) return;
  if (environment === "sandbox") Paddle.Environment.set("sandbox");
  Paddle.Initialize({
    token,
    eventCallback: (evento) => {
      if (manejadorActual) manejadorActual(evento);
    },
  });
  inicializadoCon = token;
};

// Abre el overlay. Resuelve "completado" al llegar `checkout.completed`, o
// "cerrado" si el médico lo cierra antes de pagar. Rechaza si Paddle.js no carga.
export const abrirCheckoutPaddle = async ({ config, priceId, email, customData }) => {
  const Paddle = await cargarPaddle();
  inicializar(Paddle, config);
  return new Promise((resolve) => {
    let terminado = false;
    const terminar = (resultado) => {
      if (terminado) return;
      terminado = true;
      if (manejadorActual === manejador) manejadorActual = null;
      resolve(resultado);
    };
    const manejador = (evento) => {
      if (evento?.name === "checkout.completed") terminar("completado");
      else if (evento?.name === "checkout.closed") terminar("cerrado");
    };
    manejadorActual = manejador;
    Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: email ? { email } : undefined,
      customData,
      settings: { displayMode: "overlay", locale: "es" },
    });
  });
};

// Solo para tests: vuelve al estado de página recién cargada.
export const _reiniciarPaddleParaTests = () => {
  promesaCarga = null;
  inicializadoCon = null;
  manejadorActual = null;
};
